import { useCallback } from 'react';

/**
 * Type alias for Web Storage API providers (localStorage or sessionStorage)
 */
type StorageProvider = Storage;

/**
 * A custom hook for managing data in Web Storage (localStorage/sessionStorage)
 *
 * @template T - The type of data to be stored
 * @param {StorageProvider} provider - The storage provider (localStorage/sessionStorage)
 * @param {string} key - The key under which to store the data
 * @returns {[
 *   (value: T) => void,
 *   () => T | null,
 *   () => void
 * ]} A tuple containing [setItem, getItem, removeItem] functions
 */
export const useStorage = <T>(
  provider: StorageProvider,
  key: string,
): [(value: T) => void, () => T | null, () => void] => {
  /**
   * Stores a value in the storage provider
   *
   * @param {T} value - The value to store
   * @returns {void}
   * @throws {Error} If the value cannot be stringified
   */
  const setItem = useCallback(
    (value: T): void => {
      const stringValue = JSON.stringify(value);
      provider.setItem(key, stringValue);
    },
    [provider, key],
  );

  /**
   * Retrieves a value from the storage provider
   *
   * @returns {T | null} The stored value parsed as type T, or null if not found/invalid
   */
  const getItem = useCallback((): T | null => {
    const storedValue = provider.getItem(key);
    if (storedValue === null) return null;

    try {
      return JSON.parse(storedValue) as T;
    } catch (error) {
      console.warn(`Error parsing storage value for key "${key}":`, error);
      return null;
    }
  }, [provider, key]);

  /**
   * Removes a value from the storage provider
   *
   * @returns {void}
   */
  const removeItem = useCallback((): void => {
    provider.removeItem(key);
  }, [provider, key]);

  return [setItem, getItem, removeItem];
};
